import EntryActions from 'actions/EntryActions';
import * as analytics from 'analytics/Analytics';
import BenchlingConfig from 'BenchlingConfig';
import {COLLABORATION_ANALYTICS_ACTIONS} from 'constants/analytics-products';
import DocumentActions from 'notebook/documents/DocumentActions';
import documentStore from 'notebook/documents/DocumentStore';
import TemplateActions from 'notebook/entry-templates/TemplateActions';
import TemplateStore from 'notebook/entry-templates/TemplateStore';
import {fetchAndUpdateFileLockState} from 'notebook/realtime/DocumentEditorLockReduxActions';
import {RealtimeMessageType} from 'realtime/Constants';
import {GetRealtimeMessageData} from 'realtime/RealtimeChannel';
import {getCollaborationAnalyticsMetadata} from 'realtime/util/analytics/CollaborationAnalyticsMetadata';
import {isCurrentClient} from 'realtime/util/RealtimeUtil';
import entryStore from 'stores/EntryStore';
import APIUtil from 'util/APIUtil';

import type {DocumentUpdateMessageHandler} from './DocumentUpdateMessageHandlerConstants';

function isRealtimeTemplate(fileId: string): boolean {
  return APIUtil.isEntryTemplate(fileId) && BenchlingConfig.ELN_REALTIME_TEMPLATE_UPDATES;
}

type BaseEntryStoreAndActions =
  | [typeof entryStore, typeof EntryActions]
  | [typeof documentStore, typeof DocumentActions]
  | [typeof TemplateStore, typeof TemplateActions];

function getBaseEntryStoreAndActions(fileId: string): BaseEntryStoreAndActions | null {
  if (APIUtil.isEntry(fileId)) {
    return [entryStore, EntryActions];
  } else if (APIUtil.isDocument(fileId)) {
    return [documentStore, DocumentActions];
  } else if (isRealtimeTemplate(fileId)) {
    return [TemplateStore, TemplateActions];
  }
  return null;
}

export const entryUpdateMessageHandler: DocumentUpdateMessageHandler<
  typeof RealtimeMessageType.ENTRY_UPDATE
> = {
  active: (
    fileId: string,
    {clientId}: GetRealtimeMessageData<typeof RealtimeMessageType.ENTRY_UPDATE>
  ): void => {
    fetchAndUpdateFileLockState(fileId);
    // Ignore the message if it is triggered by the current client since it should already have the latest changes
    if (clientId && !isCurrentClient(clientId)) {
      analytics.trackEvent(
        COLLABORATION_ANALYTICS_ACTIONS.RECEIVED_REALTIME_UPDATE,
        getCollaborationAnalyticsMetadata(fileId)
      );
      const storeAndActions = getBaseEntryStoreAndActions(fileId);
      if (storeAndActions == null) {
        return;
      }
      const [Store, Actions] = storeAndActions;
      if (Store.inStore(fileId)) {
        const editorState = Store.getEditorState(fileId);
        // Don't update the item if the client is viewing an old version
        if (editorState.openVersionId !== editorState.metadata?.latestVersionId) {
          return;
        }
        // Don't update the item if the current user has started editing
        if (Store.hasUnsavedChanges(fileId)) {
          return;
        }
      }
      Actions.open(fileId);
    }
  },
  inactive: (
    fileId: string,
    {clientId}: GetRealtimeMessageData<typeof RealtimeMessageType.ENTRY_UPDATE>
  ): void => {
    if (clientId && !isCurrentClient(clientId)) {
      const storeAndActions = getBaseEntryStoreAndActions(fileId);
      if (storeAndActions == null) {
        return;
      }
      const [Store, Actions] = storeAndActions;
      if (Store.inStore(fileId)) {
        Actions.postServerUpdate(fileId, {isStale: true});
      }
    }
  },
};
